import React, {useRef, useEffect, useCallback, useState} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import styled from '@emotion/styled'

import {connect} from 'react-redux'

import useMediaQuery from '@mui/material/useMediaQuery'

import {Virtuoso} from 'react-virtuoso'

import {useIsMounted} from '../../../hooks'
import {paginationSearch} from '../../../actions'
import {makeSelectSortedSearchPerformances} from '../../../selectors'


import MapListItem from './MapListItem'
import MapListNotice from './MapListNotice'

import {ThreeDotProgress} from '../../generics'




export const ItemContainer = styled.div`
  height: 230px;
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
  z-index: 3;

  @media (max-width: 600px) {
    max-width: 100vw;
    width: 100%;
    height: 170px;
  }
`

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  min-height: 100%;
  overflow-x: hidden;
  padding: 0;

  & > :last-child {
    margin-bottom: 200px;
  }

  & > :first-of-type {
    margin-top: 20px;
  }

  @media (max-width: 1200px) {
    padding: 0 10px;
  }
  @media (max-width: 600px) {
    padding: 30px 15px 0px 5px;
  }
`


const styles = {
  root: css`
    height: 100%;
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    position: relative;
    background-color: transparent;
  `,
  progressContainer: css`
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    p {
      color: #000;
      padding-top: 10px;
    }
  `,
  noScrollbars: css`
    -ms-overflow-style: none;
    scrollbar-width: none;
    scrollbar-color: transparent transparent;
    
    &::-webkit-scrollbar {
      width: 0px; 
      display: none;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: transparent;
    }
  `
}



const MapList = ({
  links,
  isFetching,
  isUserLocated,
  performanceIds,
  paginationSearch,
  isFirstSearchCompleted
}) => {

  const isMounted = useIsMounted()

  const virtuosoRef  = useRef()
  const scrollPosRef = useRef(0)

  const isXs = useMediaQuery((theme) => theme.breakpoints.only('xs'))

  const hasNextPage = (links.pages !== links.page)
  const [isFetchingMore, setIsFetchingMore] = useState(false)

  
  const initialScroll = parseInt(
    window.sessionStorage.getItem('mapListScrollPosition') || '0',
    10
  )


  const fetchMoreItems = useCallback(() => {
    if (!hasNextPage || !isMounted.current) return
    setIsFetchingMore(true)

    const url = links.next_url.slice(4)
    paginationSearch(url, 'PERFORMANCE').finally(() => {
      if (isMounted.current) setIsFetchingMore(false)
    })

    return () => {}
  }, [hasNextPage, links.next_url, paginationSearch, isMounted])



  // Reset to top on a fresh search
  useEffect(() => {
    if (virtuosoRef.current && links.page === 1) {
      virtuosoRef.current.scrollToIndex({index: 0})
    }
  }, [links.page])


  // Wire up scroll listener
  const scrollerRef = useCallback((node) => {
    if (!node) return
    node.onscroll = (e) => {
      scrollPosRef.current = e.target.scrollTop
    }
  }, [])


  // Save scroll pos on unmount
  useEffect(() => {
    return () => {
      if (scrollPosRef.current) {
        sessionStorage.setItem(
          'mapListScrollPosition',
          scrollPosRef.current.toString()
        )
      } else {
        sessionStorage.removeItem('mapListScrollPosition')
      }
    }
  }, [])



  // animate items only once
  const animatedRef = useRef(new Set())
  const itemContent = useCallback(index => {
    const id = performanceIds[index]
    const shouldAnimate = !animatedRef.current.has(id)
    animatedRef.current.add(id)
    return (
      <MapListItem key={id} performanceId={id} animate={shouldAnimate} />
    )
  }, [performanceIds])




  return (
    <div css={styles.root}>
      <Virtuoso
        ref={virtuosoRef}
        style={{height: '100%', width: '100%'}}
        css={styles.noScrollbars}
        scrollerRef={scrollerRef}
        initialScrollTop={initialScroll}
        totalCount={performanceIds.length}
        endReached={fetchMoreItems}
        fixedItemHeight={isXs ? 170 : 230}
        itemContent={itemContent}
        computeItemKey={i => performanceIds[i]}
        increaseViewportBy={{
          top: isXs ? 170 : 230,
          bottom: isXs ? 170 : 230
        }}
        components={{ 
          Item: ItemContainer,
          List: ListContainer,
        }}
      />


      {!isUserLocated &&
        <div css={styles.progressContainer}>
          <ThreeDotProgress radius={7} />
          <p>Fetching your location</p>
        </div>
      }


      {isFirstSearchCompleted && performanceIds.length === 0 && !isFetching &&
        <MapListNotice />
      }
    </div>
  )
}




const makeMapStateToProps = () => {
  const getSortedEvents = makeSelectSortedSearchPerformances()

  const mapStateToProps = (state) => {
    const events = getSortedEvents(state)

    return { 
      performanceIds: events.map(e => e.id),
      links: state.search.performance.links,
      isFetching: state.search.performance.isFetching,
      isUserLocated: state.user.location.isLocated,
      isFirstSearchCompleted: state.search.counter.performance > 0
    }
  }

  return mapStateToProps
}


export default connect(makeMapStateToProps, {paginationSearch})(MapList)




